import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { format } from 'date-fns';
import {
  Badge,
  badgeStyle,
  badgeType,
  btnSize,
  btnType,
  Button,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  Icons,
  Text,
  textLevel,
} from '../../../../packages';
import { useStyles } from '../ClaimDetail.styles';
import { Typography } from '@mui/material';
import { sentenceCase } from 'sentence-case';
import Stack from '@mui/material/Stack';
import dayjs from '../../../../utils/dayjs';

const defaultData = {
  dob: '',
  firstName: '',
  fullName: '',
  gender: '',
  lastName: '',
  phoneNumber: '',
  profileImage: '',
  ssn: '',
};

const Insurance = ({ data = defaultData, onValidateInsurance, onEditInsurance }) => {
  const classes = useStyles();
  const SectionTitle = ({ title = '' }) => (
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: 800,
        lineHeight: '20px',
        letterSpacing: '1px',
        width: '100%',
        textTransform: 'uppercase',
        color: colors.neutral900,
      }}
    >
      {title}
    </Typography>
  );

  const Col = ({ label, value, link, validate, failIcon }) => (
    <Box display="flex" flexDirection="column" minWidth="160px">
      <Text className={classes.colLabel} level={textLevel.S} weight={fontWeight.SEMI_BOLD}>
        {label}
      </Text>
      {link ? (
        <Box display="flex" flexDirection="row" gap="12px" alignItems="center" className={classes.marginTop8}>
          <Icons
            glyph={value ? 'check-circle' : 'clear-circle'}
            className={value ? classes.successIcon : classes.errorIcon}
          />
          {value ? (
            <Text
              level={textLevel.M}
              weight={fontWeight.MEDIUM}
              className={classes.linkText}
              onClick={() => {
                window.open(value, '_blank');
              }}
            >
              View
            </Text>
          ) : (
            <Text level={textLevel.M} weight={fontWeight.MEDIUM}>
              Missing
            </Text>
          )}
        </Box>
      ) : validate ? (
        <Box display="flex" flexDirection="row" gap="12px" alignItems="center" className={classes.marginTop8}>
          <Icons
            glyph={!failIcon ? 'check-circle' : 'clear-circle'}
            className={!failIcon ? classes.successIcon : classes.errorIcon}
          />
          <Text level={textLevel.M} weight={fontWeight.MEDIUM}>
            {value ? dayjs(value).format('M/D/YY') : 'Not verified'}
          </Text>
          <Text
            level={textLevel.M}
            weight={fontWeight.MEDIUM}
            className={classes.linkText}
            onClick={() => {
              validate();
            }}
          >
            Validate
          </Text>
        </Box>
      ) : (
        <Text className={classes.marginTop8} level={textLevel.M} weight={fontWeight.MEDIUM}>
          {value || 'N/A'}
        </Text>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        background: colors.white,
        boxShadow:
          '0px 4px 8px rgba(0, 0, 0, 0.04), 0px 0px 2px rgba(0, 0, 0, 0.06), 0px 0px 1px rgba(0, 0, 0, 0.04)',
        borderRadius: 2,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" pl={3} py="12px" pr={2}>
        <Stack direction="row" gap={2}>
          <Heading level={headingLevel.S} weight={fontWeight.SEMI_BOLD}>
            Insurance
          </Heading>
          {data?.insurance?.insuranceEligibilityStatus === 'complete.eligible' ? (
            <Badge style={badgeStyle.ACTIVE} variant={badgeType.FILLED} size="28px">
              Eligible
            </Badge>
          ) : (
            <Badge style={badgeStyle.DANGER} variant={badgeType.FILLED} size="28px">
              Ineligible
            </Badge>
          )}
        </Stack>
        <Button
          className={classes.btn}
          variant={btnType.TEXT}
          onClick={() => {
            onEditInsurance();
          }}
          size={btnSize.SMALL}
        >
          Edit info
        </Button>
      </Box>
      <Divider />

      <Box display="flex" p={4} flexWrap="wrap" gap={3} rowGap={4}>
        <Col label="Payer" value={data?.payer?.name} />
        {data?.insurance?.currentInsurance?.candidPayerId && (
          <Col label="Payer ID" value={data.insurance.currentInsurance.candidPayerId} />
        )}
        <Col label="Payer State" value={data?.state} />
        {data?.insurance?.insuranceEligibilityDetail?.insuranceTypeCode && (
          <Col label="Insurance Type" value={data.insurance.insuranceEligibilityDetail.insuranceTypeCode} />
        )}
        {data?.insurance?.insuranceEligibilityDetail?.planName && (
          <Col label="Plan Name" value={sentenceCase(data.insurance.insuranceEligibilityDetail.planName)} />
        )}
      </Box>
      <Box display="flex" px={4} pb={4} flexWrap="wrap" gap={3} rowGap={4}>
        <SectionTitle title="Member Info" />
        <Col label="Member Name" value={data?.subscriber?.firstName + ' ' + data?.subscriber?.lastName} />
        <Col
          label="Date of Birth"
          value={data?.subscriber?.dob ? format(new Date(data?.subscriber?.dob), 'MM.dd.yyyy') : ''}
        />
        <Col label="Gender" value={data?.subscriber?.gender} />
        <Col label="Member ID" value={data?.subscriber?.memberId} />
        <Col label="Group number" value={data?.subscriber?.groupNumber} />
        <Col label="Group Name" value={data?.subscriber?.groupName} />
        {data?.insurance?.patientInsuranceData && (
          <Col
            label="Relationship to Subscriber"
            value={
              data?.insurance?.patientInsuranceData?.primaryInsured
                ? 'Self'
                : sentenceCase(
                    data?.insurance?.patientInsuranceData?.relationshipToPrimarySubscriber ?? 'N/A',
                  )
            }
          />
        )}
      </Box>
      {data?.insurance?.patientInsuranceData && (
        <Box display="flex" px={4} pb={4} flexWrap="wrap" gap={3} rowGap={4}>
          <Col
            label="Insurance Card Front"
            value={data.insurance.patientInsuranceData.memberIdPhotoFront}
            link
          />
          <Col
            label="Insurance Card Back"
            value={data.insurance.patientInsuranceData.memberIdPhotoBack}
            link
          />
          <Col
            label="Eligibility Verified"
            value={data.insurance.insuranceEligibilityDetail?.timestamp}
            failIcon={data?.insurance?.insuranceEligibilityStatus !== 'complete.eligible'}
            validate={onValidateInsurance}
          />
        </Box>
      )}
    </Box>
  );
};

export default Insurance;
